/**
 * The one place the app turns a number, a price or an instant into text a person reads
 * (POS2-1134).
 *
 * Before this existed, formatting was duplicated across ~20 files and every copy pinned `en-GB`,
 * so a German user saw `1,234.5 kg` and `£`-positioned money whatever their language. The rule
 * now is simple: a display path calls this module and passes the active locale; nothing else
 * constructs an `Intl` formatter.
 *
 * **Machine formatters are not display paths and must never import this module.** `lib/zoned-day.ts`,
 * `lib/explore-chart.ts` and `lib/heatmap.ts` pin `en-CA`/`en-US`/`en-GB` because their output is a
 * bucket key, an API `date` param, or a lookup into an English weekday map — not text. Each carries
 * a guard comment and a `de` regression test.
 *
 * Locale is always the first argument, and always explicit. The low-churn alternative — an ambient
 * "current locale" the provider writes — was rejected because it sits outside React's render graph:
 * a component that formats numbers but does not call `t()` would keep rendering English separators
 * after a language switch, which is the exact interaction this ticket exists to make work.
 */
import { formatDistanceToNow, type Locale as DateFnsLocale } from "date-fns";
import { de, enGB } from "date-fns/locale";

import type { Locale } from "@/i18n/config";

/** `Intl.NumberFormat` throws `RangeError: Invalid currency code` on anything that is not three
 * ASCII letters, and the codes we format with come from tenant configuration — so a typo in an
 * org's currency threw during render, and the app's only error boundary is the Sentry one at the
 * root of `main.tsx`, so that replaced the whole app rather than one widget (POS2-1141).
 *
 * This is the shape check rather than a try/catch because it is the *same* predicate: all 17,576
 * three-letter uppercase combinations were measured against `Intl` and none is rejected, including
 * unassigned codes like `XYZ`; every other shape raises. Matching case-insensitively because
 * `Intl` itself accepts `gbp`, even though the API and the database only store uppercase. */
const ISO_4217 = /^[A-Za-z]{3}$/;

/**
 * The BCP-47 tag to hand `Intl`, which is not always the app's own locale code.
 *
 * `Intl.DateTimeFormat("en")` resolves to **US** conventions — `7/20/2026`, a 12-hour clock — and
 * the app has rendered `en-GB` on every one of these paths since it existed. The product already
 * treats the two as different languages: `localeNames` offers "English" and "English (US)" as
 * separate entries, and `appLocales` ships only the former. So `en` means British English here,
 * and a bare pass-through would have silently flipped every date in the product to month-first
 * (POS2-1134).
 *
 * Every other code is its own tag and passes straight through.
 */
function intlLocale(locale: Locale): string {
  return locale === "en" ? "en-GB" : locale;
}

function toDate(value: Date | string): Date {
  return typeof value === "string" ? new Date(value) : value;
}

// ── Numbers ─────────────────────────────────────────────────────────────────

/** A number in the reader's locale: `1,234.5` under `en`, `1.234,5` under `de`. */
export function formatNumber(
  locale: Locale,
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(intlLocale(locale), options).format(value);
}

/** A weight in kilograms. The unit is a symbol, not a word — German writes `kg` too — so it is
 * appended rather than translated, and only the number localises. */
export function formatWeight(locale: Locale, kg: number, maximumFractionDigits = 1): string {
  return `${formatNumber(locale, kg, { maximumFractionDigits })} kg`;
}

/**
 * Money in `currency`, or the raw code beside a plain number if the code is malformed.
 *
 * The degraded form keeps the code on purpose: a bare number on a money tile is indistinguishable
 * from a weight, whereas `GBPX 12.30` says the configuration is wrong. It is also the shape `Intl`
 * produces for a well-formed but unassigned code.
 *
 * `maximumFractionDigits` is left unset rather than defaulted to 2 so each currency keeps its own
 * precision — an explicit 2 renders `JP¥1,234.5` for a currency that has no minor unit. Pass 0
 * where whole units read better, as the heatmap's cells do.
 */
export function formatCurrency(
  locale: Locale,
  amount: number,
  currency: string,
  maximumFractionDigits?: number,
): string {
  if (!ISO_4217.test(currency)) {
    // The separator is a non-breaking space, written as an escape so it is visible in review. It
    // is what `Intl` itself puts between a code and a number (measured on the unassigned code
    // "XYZ"), so the degraded output matches the normal one — and the code cannot wrap away from
    // the figure it labels.
    return `${currency}\u00a0${formatNumber(locale, amount, { maximumFractionDigits })}`;
  }
  return new Intl.NumberFormat(intlLocale(locale), {
    style: "currency",
    currency,
    maximumFractionDigits,
  }).format(amount);
}

/** The currency symbol for an ISO code (e.g. "EUR" → "€"), never hardcoded. */
export function currencySymbol(locale: Locale, currency: string): string {
  if (!ISO_4217.test(currency)) return currency;
  const parts = new Intl.NumberFormat(intlLocale(locale), {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).formatToParts(0);
  return parts.find((p) => p.type === "currency")?.value ?? currency;
}

/** A rate in 0…1 as a percentage. `Intl` rather than `toFixed` because German puts a space before
 * the sign — `12,3 %` — and a hand-appended "%" would not. */
export function formatPercent(locale: Locale, rate: number, fractionDigits = 1): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: "percent",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(rate);
}

// ── Dates ───────────────────────────────────────────────────────────────────

/** A date with no time. No default options: bare `Intl.DateTimeFormat(locale)` is exactly what the
 * `toLocaleDateString()` calls this replaced produced, so adding a locale changed nothing else.
 * Callers wanting a style (`{ month: "short" }`) or a site timezone pass it. */
export function formatDate(
  locale: Locale,
  value: Date | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(intlLocale(locale), options).format(toDate(value));
}

/**
 * A compact civil-date range: "6–12 Jul", "28 Jun – 4 Jul", "28 Dec 2025 – 3 Jan 2026".
 *
 * Shared by every surface showing a covered period — the food spend and base covers weeks lists
 * and the KPI cards' covered-range line — because three spellings of one week is how a dashboard
 * starts to look untended.
 *
 * Formatted in UTC: these are civil date strings and `new Date("2026-09-14")` is UTC midnight, so
 * the viewer's own zone would move the label a day for anyone west of Greenwich.
 */
export function formatDateRange(locale: Locale, start: string, end: string): string {
  const day: Intl.DateTimeFormatOptions = { day: "numeric", timeZone: "UTC" };
  const dayMonth: Intl.DateTimeFormatOptions = { ...day, month: "short" };
  const full: Intl.DateTimeFormatOptions = { ...dayMonth, year: "numeric" };
  const [startYear, startMonth] = start.split("-");
  const [endYear, endMonth] = end.split("-");

  if (startYear !== endYear) {
    return `${formatDate(locale, start, full)} – ${formatDate(locale, end, full)}`;
  }
  if (startMonth !== endMonth) {
    return `${formatDate(locale, start, dayMonth)} – ${formatDate(locale, end, dayMonth)}`;
  }
  return `${formatDate(locale, start, day)}–${formatDate(locale, end, dayMonth)}`;
}

/** A date and a clock time. `dateStyle`/`timeStyle` default to what bare `toLocaleString()` gives,
 * and are overridable together with a `timeZone` — which display paths should almost always pass,
 * because the platform resolves one zone per site and the viewer's is never the answer. */
export function formatDateTime(
  locale: Locale,
  value: Date | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    dateStyle: "short",
    timeStyle: "medium",
    ...options,
  }).format(toDate(value));
}

/** "3 hours ago" / "vor 3 Stunden". */
/** The seven weekday abbreviations, Monday first, in the reader's language.
 *
 * `Intl` rather than seven catalogue keys per locale (POS2-1176): a weekday name is formatting,
 * and hand-written keys would be a second copy of a table the platform already has — one that goes
 * stale silently and for every locale added later. Monday-first because the product's week is,
 * and 2024-01-01 was a Monday.
 */
export function weekdayLabels(locale: Locale): string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: "short" });
  return Array.from({ length: 7 }, (_, i) => format.format(new Date(Date.UTC(2024, 0, 1 + i))));
}

export function formatRelative(locale: Locale, value: Date | string): string {
  return formatDistanceToNow(toDate(value), { addSuffix: true, locale: dateFnsLocale(locale) });
}

/** date-fns needs its own locale object, and ships one module per locale rather than a registry.
 *
 * Partial on purpose: `Locale` is the ten-code vocabulary, `appLocales` is the two that ship a
 * catalogue. English is the fallback for the same reason it is the catalogue's last rung — it is
 * the language the app is guaranteed to be able to render. */
const dateFnsLocales: Partial<Record<Locale, DateFnsLocale>> = { en: enGB, de };

export function dateFnsLocale(locale: Locale): DateFnsLocale {
  return dateFnsLocales[locale] ?? enGB;
}

// ── Parsing ─────────────────────────────────────────────────────────────────

/** The decimal separator `locale` writes, read off `Intl` rather than tabulated.
 *
 * Exported because it is also what decides a CSV's dialect: a locale that writes `1,5` cannot use
 * a comma to separate fields, so `lib/explore-export.ts` reads the mark rather than keeping its own
 * list of which languages those are (POS2-1134 CP4b). */
export function localeDecimal(locale: Locale): "." | "," {
  const parts = new Intl.NumberFormat(intlLocale(locale)).formatToParts(1.1);
  return parts.find((p) => p.type === "decimal")?.value === "," ? "," : ".";
}

/**
 * Which mark in `value` is the decimal point — `null` when every separator is grouping.
 *
 * The locale is the *last* resort, not the first, because a German kitchen typing `1.5` into a
 * weight field means one and a half, and locale-first parsing turns that into fifteen — a silent
 * ten-fold error on the number the whole product measures. So:
 *
 *   * both marks present → the rightmost is the decimal (`1.234,5`, `1,234.5` both parse)
 *   * one mark, repeated → grouping only (`1.234.567`)
 *   * one mark, not followed by exactly three digits → decimal, whatever the locale (`1.5`, `1,5`)
 *   * one mark followed by exactly three digits → genuinely ambiguous, and only here does the
 *     locale decide (`1.234` is a thousand in German and one-point-two-three-four in English)
 */
function decimalMark(locale: Locale, value: string): "." | "," | null {
  const dot = value.lastIndexOf(".");
  const comma = value.lastIndexOf(",");
  if (dot < 0 && comma < 0) return null;
  if (dot >= 0 && comma >= 0) return dot > comma ? "." : ",";

  const mark = dot >= 0 ? "." : ",";
  if (value.indexOf(mark) !== value.lastIndexOf(mark)) return null;
  if (!/^\d{3}$/.test(value.slice(value.lastIndexOf(mark) + 1))) return mark;
  return localeDecimal(locale) === mark ? mark : null;
}

/**
 * A number the user typed, in their own notation — `null` if it is not one.
 *
 * `Number("1,5")` is `NaN` and `parseFloat("1,5")` is `1`, so a German entering a weight either
 * saw a validation error or silently logged the wrong figure. This is the boundary that stops it
 * (pinned decision 3), applied to the numeric fields German kitchen staff actually type into.
 */
export function parseLocaleNumber(locale: Locale, input: string): number | null {
  // Spaces are grouping in several locales and are what a paste from a spreadsheet carries:
  // ordinary, non-breaking, and narrow no-break.
  const value = input.replace(/[\s\u00a0\u202f]/g, "");
  if (!/^-?\d[\d.,]*$/.test(value)) return null;

  const mark = decimalMark(locale, value);
  const normalised = mark
    ? value.replace(mark === "." ? /,/g : /\./g, "").replace(mark, ".")
    : value.replace(/[.,]/g, "");

  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? parsed : null;
}
